/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(function () {
  const normalize = (el) => {
    const raw = el.textContent || '';
    const digits = raw.replace(/\D/g, '');
    if (digits && digits !== raw) el.textContent = digits;
  };

  const apply = () => {
    document.querySelectorAll('.wishlist-hero-items-count').forEach(normalize);
  };

  const observer = new MutationObserver(apply);

  const start = () => {
    apply();
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

(function () {
  document.addEventListener('click', (evt) => {
    const wishlistLink = evt.target.closest('.wishlist-hero-header-link');
    if (!wishlistLink) return;
    evt.stopImmediatePropagation();
    evt.preventDefault();
    window.location.href = '/a/wishlist-hero/wishlist';
  }, true);
})();

/*
 * Style Quiz
 *
 * Data is the single source of truth: snippets/style-quiz-data.liquid is
 * rendered inline as a <script type="application/json" data-style-quiz-data>
 * inside the section. Questions, answers, filter rules, UI strings, and
 * answer thumbnails all live there. To change the quiz, edit that snippet.
 *
 * Broken/missing answer images fall back to the no-image layout: on <img>
 * error, the thumb wrapper is removed so the button collapses to text-only,
 * identical to an answer that never had an image set.
 */
(function () {
  'use strict';

  const QUIZ_COLLECTION_PARAM_KEY = 'from_quiz';
  const QUIZ_COLLECTION_PARAM_VALUE = '1';

  // Shared escaping helpers (used by both the quiz and the results scorer).
  function escapeAttr(value) {
    return String(value).replace(/"/g, '&quot;');
  }
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  const DEFAULT_UI = {
    stepLabel: 'Schritt {step} von {total}',
    remainingOne: 'Nur noch 1 Schritt',
    remainingMany: 'Nur noch {n} Schritte',
    almostDone: 'Fast geschafft',
    nextLabel: 'Weiter',
    finishLabel: 'Ergebnisse ansehen',
    backLabel: 'Zurück',
    resultCtaLabel: 'Ansehen',
    selectHint: 'Bitte wähle eine Option'
  };

  class PMGWStyleQuiz {
    constructor(sectionElement) {
      this.root = sectionElement;
      this.sectionId = sectionElement.dataset.sectionId || '';
      this.debugMode = sectionElement.dataset.debugMode === 'true';
      this.redirectTimeout = null;

      this.currentQuestionIndex = -1;
      this.answers = {};

      const data = this.loadData();
      this.ui = Object.assign({}, DEFAULT_UI, (data && data.ui) || {});
      this.baseCollectionUrl = (data && data.baseCollectionUrl) || '/collections/style-quiz';
      this.introImage = (data && data.introImage) || '';
      this.questions = this.normalizeQuestions((data && data.questions) || []);
      this.requiredKeys = this.questions.map((q) => q.key);
      this.totalSteps = this.questions.length;

      this.panels = {
        intro: this.root.querySelector('[data-quiz-panel="intro"]'),
        question: this.root.querySelector('[data-quiz-panel="question"]')
      };
      this.progressWrap = this.root.querySelector('[data-quiz-progress-wrap]');
      this.progressText = this.root.querySelector('[data-quiz-progress-text]');
      this.progressRemaining = this.root.querySelector('[data-quiz-progress-remaining]');
      this.progressBar = this.root.querySelector('[data-quiz-progress-bar]');
      this.progressBarWrap = this.root.querySelector('[data-quiz-progress]');
      this.questionHeading = this.root.querySelector('[data-quiz-question]');
      this.answersWrap = this.root.querySelector('[data-quiz-answers]');
      this.debugOutput = this.root.querySelector('[data-quiz-debug]');
      this.nextButton = this.root.querySelector('[data-quiz-next]');
      this.hintEl = this.root.querySelector('[data-quiz-hint]');
      this.backButton = this.root.querySelector('[data-quiz-back-label]');

      // Inline result view: scored recommendation shown in place of the quiz
      // (no page navigation). The pool + the value→signals lookup feed scoring.
      this.layoutEl = this.root.querySelector('.pmgw-style-quiz-layout');
      this.resultEl = this.root.querySelector('[data-quiz-result]');
      this.resultHero = this.root.querySelector('[data-result-hero]');
      this.resultComplements = this.root.querySelector('[data-result-complements]');
      this.pool = this.parsePool();
      this.signalsByValue = this.buildSignalLookup(this.questions);
      this.reduceMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Side image swaps per step. Remember the server-rendered src + srcset so
      // the intro can be restored, and so steps without their own image fall
      // back to it instead of going blank.
      this.sideImage = this.root.querySelector('.pmgw-style-quiz-side__image');
      this.sideImageDefaultSrcset = this.sideImage ? this.sideImage.getAttribute('srcset') : '';
      // introImage (from the data snippet) is the intro / fallback side image.
      // When set it overrides the theme-editor side_image so the data file is
      // the single source of truth; it also becomes the restore target.
      if (this.introImage && this.sideImage) {
        this.sideImage.removeAttribute('srcset');
        this.sideImageDefaultSrcset = '';
        this.sideImage.setAttribute('src', this.introImage);
        this.sideImageDefaultSrc = this.introImage;
      } else {
        this.sideImageDefaultSrc = this.sideImage ? this.sideImage.getAttribute('src') : '';
      }

      // True cross-fade needs two stacked layers so the cream background never
      // shows between images. The server-rendered <img> is the base; we add a
      // second <img> on top that the incoming image fades in over. Once the
      // fade completes the base adopts the new src and the overlay hides.
      if (this.sideImage) {
        const overlay = this.sideImage.cloneNode(false);
        overlay.classList.add('pmgw-style-quiz-side__image--overlay');
        overlay.removeAttribute('srcset');
        overlay.removeAttribute('src');
        overlay.setAttribute('alt', '');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.style.opacity = '0';
        this.sideImage.parentNode.appendChild(overlay);
        this.sideImageOverlay = overlay;
      }

      if (this.progressBarWrap) {
        this.progressBarWrap.setAttribute('aria-valuemax', String(this.totalSteps || 1));
      }
      if (this.backButton) {
        this.backButton.textContent = this.ui.backLabel;
      }
      if (this.nextButton) {
        this.nextButton.textContent = this.ui.nextLabel;
      }

      this.handleClick = this.handleClick.bind(this);
      this.handleImageError = this.handleImageError.bind(this);
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('error', this.handleImageError, true);

      this.preloadImages();

      // Shared/bookmarked links and the browser back button carry the answers
      // in ?quiz=; restore straight to the result view when present.
      this.handlePopState = this.handlePopState.bind(this);
      window.addEventListener('popstate', this.handlePopState);
      this.restoreFromUrl();
    }

    parsePool() {
      const node = this.root.querySelector('[data-quiz-pool]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent.trim()) || [];
      } catch (error) {
        this.logDebug('Pool parse failed', error);
        return [];
      }
    }

    // Flatten quiz data to { answerValue: signalsObject } so a ?quiz= token
    // resolves to its weighted signals regardless of which question it came
    // from (answer values are unique across the quiz).
    buildSignalLookup(questions) {
      const map = {};
      questions.forEach((q) => {
        q.answers.forEach((a) => {
          if (a && a.value) map[String(a.value)] = a.signals || {};
        });
      });
      return map;
    }

    loadData() {
      const node = this.root.querySelector('[data-style-quiz-data]');
      if (!node) {
        this.logDebug('No data node found; quiz cannot render.');
        return null;
      }
      try {
        return JSON.parse(node.textContent.trim());
      } catch (error) {
        this.logDebug('Quiz data parse failed', error);
        return null;
      }
    }

    normalizeQuestions(questions) {
      return questions.map((question) => ({
        key: question.key || '',
        selectionMode: question.selectionMode === 'multiple' ? 'multiple' : 'single',
        question: question.question || '',
        sideImage: question.sideImage || '',
        sideImagePosition: question.sideImagePosition || '',
        answers: (question.answers || []).map((answer) => ({
          value: answer.value ? String(answer.value) : '',
          label: answer.label || '',
          image: answer.image || '',
          subtitle: answer.subtitle || '',
          filters: answer.filters || {},
          signals: answer.signals || {}
        }))
      }));
    }

    handleClick(event) {
      const actionButton = event.target.closest('[data-quiz-action]');
      const answerButton = event.target.closest('[data-quiz-answer]');

      if (actionButton) {
        const action = actionButton.dataset.quizAction;
        if (action === 'start') this.startQuiz();
        else if (action === 'back') this.goBack();
        else if (action === 'next') this.goNext();
        else if (action === 'restart') this.restartQuiz();
      }
      if (answerButton) this.selectAnswer(answerButton);
    }

    handleImageError(event) {
      const img = event.target;
      if (!(img instanceof HTMLImageElement)) return;
      const wrap = img.closest('.pmgw-style-quiz__answer-thumb-wrap');
      if (wrap) {
        wrap.remove();
      } else {
        img.remove();
      }
    }

    setSideImage(src, position) {
      if (!this.sideImage) return;
      const base = this.sideImage;
      const overlay = this.sideImageOverlay;
      const next = src || this.sideImageDefaultSrc;

      // Focal point follows the image even when the src is unchanged (two
      // consecutive steps can share an image but want different crops).
      base.style.objectPosition = position || '';
      if (overlay) overlay.style.objectPosition = position || '';

      if (base.getAttribute('src') === next) return;

      // No overlay layer (shouldn't happen) → plain swap, no fade.
      if (!overlay) {
        base.setAttribute('src', next);
        return;
      }

      // Cancel any in-flight transition so rapid next/back taps don't stack.
      if (this._sideFadeTimer) {
        window.clearTimeout(this._sideFadeTimer);
        overlay.removeEventListener('transitionend', this._sideFadeEnd);
      }

      // Soft slide + fade. A plain opacity dissolve between two different
      // photos muddies at the 50% midpoint (ghosty double-exposure). Offsetting
      // them in space avoids that: the new image rises in from just below while
      // the old one rises up and out. The overscan (scale 1.06 in CSS) keeps the
      // cream box edge hidden during the ~14px travel.
      const SLIDE = 14;

      // Stage the overlay below + transparent with transitions suppressed.
      overlay.style.transition = 'none';
      overlay.style.setProperty('--pmgw-img-slide', SLIDE + 'px');
      overlay.style.opacity = '0';
      overlay.setAttribute('src', next);
      void overlay.offsetWidth; // commit the start state before animating

      const settle = () => {
        overlay.removeEventListener('transitionend', this._sideFadeEnd);
        if (this._sideFadeTimer) { window.clearTimeout(this._sideFadeTimer); this._sideFadeTimer = null; }
        // The overlay now fully covers the base. Promote its image onto the
        // base and reset both layers to the resting state with transitions
        // suppressed (no visible change, since both show identical pixels).
        const baseTransition = base.style.transition;
        const overlayTransition = overlay.style.transition;
        base.style.transition = 'none';
        overlay.style.transition = 'none';
        base.removeAttribute('srcset');
        base.setAttribute('src', next);
        base.style.opacity = '';
        base.style.setProperty('--pmgw-img-slide', '0px');
        overlay.style.opacity = '0';
        overlay.style.setProperty('--pmgw-img-slide', SLIDE + 'px');
        void overlay.offsetWidth;
        base.style.transition = baseTransition;
        overlay.style.transition = overlayTransition;
      };

      this._sideFadeEnd = (e) => {
        if (e.propertyName !== 'opacity') return;
        settle();
      };

      // Next frame: re-enable transitions and animate. Overlay rises to rest
      // and fades in; the base rises up and out.
      requestAnimationFrame(() => {
        overlay.style.transition = '';
        overlay.addEventListener('transitionend', this._sideFadeEnd);
        overlay.style.setProperty('--pmgw-img-slide', '0px');
        overlay.style.opacity = '1';
        base.style.setProperty('--pmgw-img-slide', '-' + SLIDE + 'px');
        base.style.opacity = '0';
      });

      // Fallback in case transitionend doesn't fire.
      this._sideFadeTimer = window.setTimeout(settle, 640);
    }

    // Warm the browser cache for every per-step side image + answer thumbnail
    // up front, so advancing to the next question shows its image instantly
    // instead of fetching it on demand (the noticeable navigation delay).
    preloadImages() {
      const urls = [];
      this.questions.forEach((q) => {
        if (q.sideImage) urls.push(q.sideImage);
        q.answers.forEach((a) => { if (a.image) urls.push(a.image); });
      });
      Array.from(new Set(urls)).forEach((url) => {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
      });
    }

    startQuiz() {
      if (!this.questions.length) {
        this.logDebug('No questions found.');
        return;
      }
      this.currentQuestionIndex = 0;
      this.renderQuestion(true);
      this.showPanel('question');
    }

    goBack() {
      this.clearRedirectTimeout();
      if (this.currentQuestionIndex <= 0) {
        this.currentQuestionIndex = -1;
        this.updateProgress();
        this.setSideImage('');
        this.showPanel('intro');
        return;
      }
      this.currentQuestionIndex -= 1;
      this.renderQuestion(true);
      this.showPanel('question');
    }

    selectAnswer(answerButton) {
      const questionKey = answerButton.dataset.quizQuestion;
      const answerValue = answerButton.dataset.quizAnswer;
      const answerLabel = answerButton.dataset.quizLabel;
      if (!questionKey || !answerValue) return;

      const question = this.questions[this.currentQuestionIndex];
      if (!question) return;

      const current = this.answers[questionKey] || { values: [], labels: [] };
      const values = current.values.slice();
      const labels = current.labels.slice();

      if (question.selectionMode === 'multiple') {
        const existing = values.indexOf(answerValue);
        if (existing >= 0) {
          values.splice(existing, 1);
          labels.splice(existing, 1);
          answerButton.classList.remove('is-selected');
        } else {
          values.push(answerValue);
          labels.push(answerLabel || answerValue);
          answerButton.classList.add('is-selected');
        }
      } else {
        // Single-select: clear the previously chosen sibling, mark this one.
        const prev = this.answersWrap.querySelector('.pmgw-style-quiz__answer.is-selected');
        if (prev && prev !== answerButton) prev.classList.remove('is-selected');
        values.length = 0;
        labels.length = 0;
        values.push(answerValue);
        labels.push(answerLabel || answerValue);
        answerButton.classList.add('is-selected');
      }

      this.answers[questionKey] = { values: values, labels: labels };
      // Update only the things that depend on selection state — never rebuild
      // the answer DOM (that caused a full flicker on every click and replaced
      // the node mid-interaction). The cards stay put; just the Next button.
      this.updateStepActions();
    }

    renderQuestion(animateStep) {
      const question = this.questions[this.currentQuestionIndex];
      if (!question || !this.questionHeading || !this.answersWrap) return;

      this.setSideImage(question.sideImage, question.sideImagePosition);
      this.questionHeading.textContent = question.question;
      this.answersWrap.classList.toggle('pmgw-style-quiz__answers--multiple', question.selectionMode === 'multiple');
      this.answersWrap.innerHTML = question.answers.map((answer, index) => {
        const selectedValues = (this.answers[question.key] || { values: [] }).values;
        const isSelected = selectedValues.indexOf(answer.value) >= 0;
        const hasImage = Boolean(answer.image);
        const hasSubtitle = Boolean(answer.subtitle);
        return [
          '<button class="pmgw-style-quiz__answer' + (isSelected ? ' is-selected' : '') + '" type="button"',
          ' style="--i:' + index + '"',
          ' data-quiz-answer="' + this.escapeAttr(answer.value) + '"',
          ' data-quiz-question="' + this.escapeAttr(question.key) + '"',
          ' data-quiz-label="' + this.escapeAttr(answer.label) + '"',
          ' data-selection-mode="' + this.escapeAttr(question.selectionMode) + '">',
          hasImage
            ? '<span class="pmgw-style-quiz__answer-thumb-wrap"><img class="pmgw-style-quiz__answer-thumb" src="' + this.escapeAttr(answer.image) + '" alt="" loading="lazy"></span>'
            : '',
          '<span class="pmgw-style-quiz__answer-content">',
          '<span class="pmgw-style-quiz__answer-label">' + this.escapeHtml(answer.label) + '</span>',
          hasSubtitle ? '<span class="pmgw-style-quiz__answer-subtitle">' + this.escapeHtml(answer.subtitle) + '</span>' : '',
          '</span>',
          '<span class="pmgw-style-quiz__answer-icon" aria-hidden="true"></span>',
          '</button>'
        ].join('');
      }).join('');

      this.updateStepActions();
      this.updateProgress();

      // Re-trigger the step-change animation on the question panel. Only on
      // actual step changes (start / next / back), not on every answer toggle.
      if (animateStep && this.panels.question) {
        const panel = this.panels.question;
        panel.classList.remove('pmgw-style-quiz__panel--enter');
        // Force reflow so removing + re-adding the class restarts the animation.
        void panel.offsetWidth;
        panel.classList.add('pmgw-style-quiz__panel--enter');
      }
    }

    goNext() {
      if (this.currentQuestionIndex < 0) return;
      if (!this.hasValidSelection(this.currentQuestionIndex)) return;

      const isLast = this.currentQuestionIndex >= this.questions.length - 1;
      if (isLast) {
        this.redirectToResult();
        return;
      }
      this.currentQuestionIndex += 1;
      this.renderQuestion(true);
    }

    updateProgress() {
      if (!this.progressWrap || !this.progressText || !this.progressBar || !this.progressBarWrap) return;
      if (this.currentQuestionIndex < 0) {
        this.progressWrap.hidden = true;
        return;
      }
      this.progressWrap.hidden = false;
      const step = this.currentQuestionIndex + 1;
      const percent = this.totalSteps > 0 ? (step / this.totalSteps) * 100 : 0;
      this.progressText.textContent = this.formatString(this.ui.stepLabel, { step: step, total: this.totalSteps });
      this.progressBar.style.width = percent + '%';
      this.progressBarWrap.setAttribute('aria-valuenow', String(step));

      const remaining = this.totalSteps - step;
      if (remaining <= 0) {
        this.progressRemaining.textContent = this.ui.almostDone;
      } else if (remaining === 1) {
        this.progressRemaining.textContent = this.ui.remainingOne;
      } else {
        this.progressRemaining.textContent = this.formatString(this.ui.remainingMany, { n: remaining });
      }
    }

    showPanel(panelName) {
      Object.keys(this.panels).forEach((name) => {
        const panel = this.panels[name];
        if (!panel) return;
        const isActive = name === panelName;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });
    }

    isComplete() {
      return this.requiredKeys.every((key) => {
        const state = this.answers[key];
        return state && Array.isArray(state.values) && state.values.length > 0;
      });
    }

    redirectToResult() {
      if (!this.isComplete()) {
        this.logDebug('Result blocked: missing answers', this.answers);
        return;
      }
      // Inline: render the curated result in place of the quiz (no navigation).
      // Push the answers into the URL (?quiz=) so the result is shareable and
      // survives reload / back-forward.
      const quiz = this.buildSignalsParam();
      this.renderDebugOutput({ quiz: quiz });

      if (window.history && window.history.pushState) {
        const url = window.location.pathname + '?quiz=' + encodeURIComponent(quiz);
        window.history.pushState({ pmgwQuiz: quiz }, '', url);
      }
      this.showResult();
    }

    // Render the scored result into the inline result view and reveal it.
    showResult() {
      if (!this.resultEl) return;
      const ranked = this.rankPool();
      const picks = this.pickHeroAndComplements(ranked);
      if (!picks.hero) {
        this.logDebug('No products to recommend; staying on quiz.');
        return;
      }

      if (this.resultHero) this.resultHero.innerHTML = this.resultCardHtml(picks.hero, true);
      if (this.resultComplements) {
        this.resultComplements.innerHTML = picks.complements.map((e) => this.resultCardHtml(e, false)).join('');
      }

      // Swap the quiz layout for the result view.
      if (this.layoutEl) this.layoutEl.hidden = true;
      this.resultEl.hidden = false;
      this.resultEl.classList.add('is-active');

      if (!this.reduceMotion) {
        const items = [this.resultHero].concat(
          this.resultComplements ? Array.from(this.resultComplements.children) : []
        );
        items.forEach((el, i) => {
          if (!el) return;
          el.style.setProperty('--i', i);
          el.classList.remove('pmgw-quiz-result__reveal');
          void el.offsetWidth;
          el.classList.add('pmgw-quiz-result__reveal');
        });
      }
    }

    // Return to the quiz from the result view (restart button / back button).
    showQuiz() {
      if (this.resultEl) {
        this.resultEl.hidden = true;
        this.resultEl.classList.remove('is-active');
      }
      if (this.layoutEl) this.layoutEl.hidden = false;
    }

    restartQuiz() {
      this.answers = {};
      this.currentQuestionIndex = -1;
      if (window.history && window.history.pushState) {
        window.history.pushState({}, '', window.location.pathname);
      }
      this.updateProgress();
      this.setSideImage('');
      this.showQuiz();
      this.showPanel('intro');
    }

    handlePopState() {
      const params = new URLSearchParams(window.location.search || '');
      if (params.get('quiz')) {
        this.restoreFromUrl();
      } else {
        // Back out of the result → quiz intro.
        this.showQuiz();
        this.showPanel('intro');
      }
    }

    // On load (or popstate), if ?quiz= is present, rebuild answers from it and
    // jump straight to the result view.
    restoreFromUrl() {
      const params = new URLSearchParams(window.location.search || '');
      const quiz = params.get('quiz');
      if (!quiz) return;
      this.answers = this.parseQuizParam(quiz);
      this.showResult();
    }

    parseQuizParam(raw) {
      const out = {};
      raw.split(';').forEach((group) => {
        const idx = group.indexOf(':');
        if (idx < 0) return;
        const key = group.slice(0, idx);
        const values = group.slice(idx + 1).split(',').filter(Boolean);
        if (!key || !values.length) return;
        out[key] = { values: values, labels: values.slice() };
      });
      return out;
    }

    // --- Scoring -----------------------------------------------------------
    // Union the selected answers' signals into one criteria object.
    deriveCriteria() {
      const c = {
        collections: new Set(),   // Q1 dress look (+ any collection signals): membership scores
        effectTags: new Set(),    // effect question: tag signal layered on top
        focusTypes: new Set(),    // Q3 jewellery type
        wantsSet: false,
        metal: null,              // Q2: 'Gold' | 'Silber' | null — a HARD FILTER, not a score
        priceMin: null, priceMax: null, priceBounded: false, priceMaxUnbounded: false
      };
      Object.keys(this.answers).forEach((qKey) => {
        const state = this.answers[qKey];
        const values = state && Array.isArray(state.values) ? state.values : [];
        values.forEach((value) => {
          const s = this.signalsByValue[value];
          if (!s) return;
          (s.collections || []).forEach((h) => c.collections.add(h));
          (s.effectTags || []).forEach((t) => c.effectTags.add(t));
          (s.productTypes || []).forEach((t) => c.focusTypes.add(t));
          if (s.isSet) c.wantsSet = true;
          if (s.metal === 'Gold' || s.metal === 'Silber') c.metal = s.metal;
          if (typeof s.priceMin === 'number') {
            c.priceBounded = true;
            c.priceMin = c.priceMin === null ? s.priceMin : Math.min(c.priceMin, s.priceMin);
          }
          if (typeof s.priceMax === 'number') {
            c.priceBounded = true;
            c.priceMax = c.priceMax === null ? s.priceMax : Math.max(c.priceMax, s.priceMax);
          } else if (s.priceMax === null && Object.prototype.hasOwnProperty.call(s, 'priceMax')) {
            c.priceBounded = true;
            c.priceMaxUnbounded = true;
          }
        });
      });
      return c;
    }

    // Whether a product is available in the chosen metal. Multi-finish products
    // list their finishes; single-finish products list one (or none, if the
    // metafield is absent — those are treated as available in either, so a
    // metal choice never hides an untagged-finish piece). Returns the matched
    // finish label (for the variant deep-link) or null.
    finishMatch(p, metal) {
      const finishes = Array.isArray(p.finishes) ? p.finishes : [];
      if (!finishes.length) return { available: true, matched: null }; // unknown finish → don't exclude
      const has = finishes.indexOf(metal) >= 0;
      return { available: has, matched: has ? metal : null };
    }

    scoreProduct(p, c) {
      let score = 0;
      const reasons = [];
      const tags = Array.isArray(p.tags) ? p.tags : [];
      const colls = Array.isArray(p.collections) ? p.collections : [];

      // Q1 dress look: binary — a product earns the points once if it's in ANY
      // of the answer's collections, regardless of how many it matches.
      let collHit = false;
      c.collections.forEach((h) => { if (!collHit && colls.indexOf(h) >= 0) collHit = true; });
      if (collHit) { score += RESULT_WEIGHTS.collectionMatch; reasons.push('Passt zu deinem Look'); }

      // Q3 jewellery type.
      if (c.focusTypes.size && c.focusTypes.has(p.type)) {
        score += RESULT_WEIGHTS.focusType;
        reasons.push(p.type);
      }

      // Effect tags (Elegant / Glamourös), layered.
      c.effectTags.forEach((t) => { if (tags.indexOf(t) >= 0) score += RESULT_WEIGHTS.effectTag; });

      // Metal: a filter (applied in rankPool), but if chosen and matched, note
      // the finish for the variant deep-link + a chip.
      let matchedFinish = null;
      if (c.metal) {
        const fm = this.finishMatch(p, c.metal);
        if (fm.matched) { matchedFinish = fm.matched; reasons.push('In ' + matchedFinish); }
      }

      if (c.priceBounded) {
        const euros = p.price / 100;
        const minOk = c.priceMin === null || euros >= c.priceMin;
        const maxOk = c.priceMaxUnbounded || c.priceMax === null || euros <= c.priceMax;
        if (minOk && maxOk) {
          score += RESULT_WEIGHTS.budgetIn;
          reasons.push('In deinem Budget');
        } else {
          score += RESULT_WEIGHTS.budgetOut;
        }
      }

      return { product: p, score: score, reasons: reasons, matchedFinish: matchedFinish };
    }

    rankPool() {
      const c = this.deriveCriteria();
      const candidates = this.filterCandidates(c);
      const scored = candidates.map((p) => this.scoreProduct(p, c));
      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (a.product.isSet !== b.product.isSet) return a.product.isSet ? -1 : 1;
        return (a.product.price || 0) - (b.product.price || 0);
      });
      return scored;
    }

    // Two HARD filters: jewellery type (Q3) and metal (Q2). The shopper asked
    // for a necklace → only necklaces; asked for gold → only gold-available.
    // Never fully empty: if both filters together leave nothing, relax metal
    // first, then type, until something remains.
    filterCandidates(c) {
      const byType = (p) => !c.focusTypes.size || c.focusTypes.has(p.type);
      const byMetal = (p) => !c.metal || this.finishMatch(p, c.metal).available;

      let out = this.pool.filter((p) => byType(p) && byMetal(p));
      if (out.length) return out;
      // Relax metal, keep type strict.
      out = this.pool.filter(byType);
      if (out.length) return out;
      // Relax type too (last resort) — keep metal if it helps, else everything.
      out = this.pool.filter(byMetal);
      return out.length ? out : this.pool;
    }

    // Hero = best of the filtered candidates. Complements should feel like a
    // styled set "to go with it" — so when the chosen type(s) collapse the
    // filtered pool to a single type (e.g. necklace-only), we deliberately pull
    // complements from OUTSIDE the type filter: other product types that share
    // the hero's look (its collections), so the row stays varied instead of
    // showing four near-identical necklaces.
    pickHeroAndComplements(ranked) {
      if (!ranked.length) return { hero: null, complements: [] };
      const hero = ranked[0];
      const heroColls = Array.isArray(hero.product.collections) ? hero.product.collections : [];
      const complements = [];
      const usedIds = new Set([hero.product.id]);
      const seenTypes = new Set([hero.product.type]);

      // How many distinct types does the filtered result actually offer? If the
      // shopper picked several types, the filtered list already varies and we
      // stay within it; if it's effectively one type, we broaden.
      const filteredTypes = new Set(ranked.map((r) => r.product.type));
      const sharesLook = (p) => heroColls.some((h) => (p.collections || []).indexOf(h) >= 0);

      const addFrom = (list, requireNewType) => {
        list.forEach((r) => {
          if (complements.length >= 3) return;
          if (usedIds.has(r.product.id)) return;
          if (requireNewType && seenTypes.has(r.product.type)) return;
          complements.push(r);
          usedIds.add(r.product.id);
          seenTypes.add(r.product.type);
        });
      };

      // 1) Varied types from within the filtered (scored) result.
      addFrom(ranked.slice(1), true);

      // 2) If still short AND the filtered result is essentially one type,
      //    broaden: score the WHOLE pool, prefer pieces that share the hero's
      //    look but are a different type (a curated "passt dazu" set).
      if (complements.length < 3 && filteredTypes.size <= 1) {
        const c = this.deriveCriteria();
        const broadened = this.pool
          .map((p) => this.scoreProduct(p, c))
          .filter((r) => !usedIds.has(r.product.id) && r.product.type !== hero.product.type && sharesLook(r.product))
          .sort((a, b) => (b.score - a.score) || ((a.product.price || 0) - (b.product.price || 0)));
        addFrom(broadened, true);
      }

      // 3) Last fill: anything remaining from the filtered result (keeps the
      //    row complete even if broadening found nothing).
      addFrom(ranked.slice(1), false);

      return { hero: hero, complements: complements };
    }

    // Append ?variant=<id> when a finish matched, so the shopper lands on the
    // chosen finish pre-selected.
    productHref(entry) {
      const p = entry.product;
      if (entry.matchedFinish && p.variantsByFinish && p.variantsByFinish[entry.matchedFinish]) {
        const sep = p.url.indexOf('?') >= 0 ? '&' : '?';
        return p.url + sep + 'variant=' + p.variantsByFinish[entry.matchedFinish];
      }
      return p.url;
    }

    resultCardHtml(entry, isHero) {
      const p = entry.product;
      const cls = isHero ? 'pmgw-quiz-result__card pmgw-quiz-result__card--hero' : 'pmgw-quiz-result__card';
      const chips = isHero && entry.reasons.length
        ? '<span class="pmgw-quiz-result__chips">'
          + entry.reasons.map((r) => '<span class="pmgw-quiz-result__chip">' + escapeHtml(r) + '</span>').join('')
          + '</span>'
        : '';
      const img = p.image
        ? '<span class="pmgw-quiz-result__card-media"><img src="' + escapeAttr(p.image) + '" alt="" loading="lazy"></span>'
        : '<span class="pmgw-quiz-result__card-media"></span>';
      // Hero gets a visible CTA affordance (the whole card is the link, so this
      // is a styled span, not a nested interactive element). Forward action at
      // the emotional peak: view the recommended piece.
      const cta = isHero
        ? '<span class="pmgw-quiz-result__card-cta">' + escapeHtml(this.ui.resultCtaLabel || 'Ansehen') + '</span>'
        : '';
      return '<a class="' + cls + '" href="' + escapeAttr(this.productHref(entry)) + '">'
        + img
        + '<span class="pmgw-quiz-result__card-body">'
        + '<span class="pmgw-quiz-result__card-title">' + escapeHtml(p.title) + '</span>'
        + '<span class="pmgw-quiz-result__card-price">' + escapeHtml(p.priceFormatted || '') + '</span>'
        + chips
        + cta
        + '</span>'
        + '</a>';
    }

    // Serialize answers to a compact, shareable string the results page
    // re-resolves against its own copy of the quiz data:
    //   style:classic,boho;focus:necklace,earrings;material:gold;budget:under50
    buildSignalsParam() {
      const parts = [];
      this.requiredKeys.forEach((key) => {
        const state = this.answers[key];
        const values = state && Array.isArray(state.values) ? state.values : [];
        if (values.length) parts.push(key + ':' + values.join(','));
      });
      return parts.join(';');
    }

    buildResultUrl() {
      const collected = { tags: [], productTypes: [], metafields: [], priceMin: null, priceMax: null };
      const debugMeta = { applied: [], skipped: [] };

      Object.keys(this.answers).forEach((questionKey) => {
        const state = this.answers[questionKey];
        const values = state && Array.isArray(state.values) ? state.values : [];
        if (!values.length) {
          debugMeta.skipped.push(questionKey + ':none');
          return;
        }
        const question = this.questions.find((q) => q.key === questionKey);
        if (!question) return;

        values.forEach((value) => {
          const answer = question.answers.find((a) => a.value === value);
          const filters = (answer && answer.filters) || {};
          if (Array.isArray(filters.tags)) {
            collected.tags = collected.tags.concat(filters.tags);
            debugMeta.applied.push(questionKey + '.tags');
          }
          if (Array.isArray(filters.productTypes)) {
            collected.productTypes = collected.productTypes.concat(filters.productTypes);
            debugMeta.applied.push(questionKey + '.productTypes');
          }
          if (typeof filters.priceMin === 'number') {
            collected.priceMin = collected.priceMin === null ? filters.priceMin : Math.min(collected.priceMin, filters.priceMin);
            debugMeta.applied.push(questionKey + '.priceMin');
          }
          if (typeof filters.priceMax === 'number') {
            collected.priceMax = collected.priceMax === null ? filters.priceMax : Math.max(collected.priceMax, filters.priceMax);
            debugMeta.applied.push(questionKey + '.priceMax');
          } else if (filters.priceMax === null && Object.prototype.hasOwnProperty.call(filters, 'priceMax')) {
            // Explicit null = "no upper bound": clear the cap.
            collected.priceMax = null;
          }
          if (Array.isArray(filters.metafields) && filters.metafields.length) {
            collected.metafields = collected.metafields.concat(filters.metafields);
            debugMeta.applied.push(questionKey + '.metafields');
          }
        });
      });

      const tags = Array.from(new Set(collected.tags));
      const productTypes = Array.from(new Set(collected.productTypes));
      const baseParts = String(this.baseCollectionUrl).split('?');
      const basePath = baseParts[0] || '/collections/style-quiz';
      const params = new URLSearchParams(baseParts[1] || '');

      tags.forEach((t) => params.append('filter.p.tag', t));
      productTypes.forEach((t) => params.append('filter.p.product_type', t));
      if (typeof collected.priceMin === 'number') params.set('filter.v.price.gte', String(collected.priceMin));
      if (typeof collected.priceMax === 'number') params.set('filter.v.price.lte', String(collected.priceMax));
      collected.metafields.forEach((mf) => {
        if (!mf || !mf.namespace || !mf.key || mf.value === undefined || mf.value === null) return;
        params.append('filter.p.m.' + mf.namespace + '.' + mf.key, String(mf.value));
      });
      params.set(QUIZ_COLLECTION_PARAM_KEY, QUIZ_COLLECTION_PARAM_VALUE);
      params.set('quiz_source', 'pmgw-style-quiz');

      const query = params.toString();
      return {
        url: query ? basePath + '?' + query : basePath,
        baseUrl: basePath,
        debugMeta: debugMeta
      };
    }

    renderDebugOutput(urlData) {
      if (!this.debugMode || !this.debugOutput) return;
      const payload = { answers: this.answers, result: urlData || this.buildResultUrl() };
      this.debugOutput.hidden = false;
      this.debugOutput.textContent = JSON.stringify(payload, null, 2);
      this.logDebug('Quiz debug payload', payload);
    }

    logDebug() {
      if (!this.debugMode || !window.console || !window.console.log) return;
      const args = Array.prototype.slice.call(arguments);
      args.unshift('[PMGWStyleQuiz:' + this.sectionId + ']');
      window.console.log.apply(window.console, args);
    }

    clearRedirectTimeout() {
      if (this.redirectTimeout) {
        window.clearTimeout(this.redirectTimeout);
        this.redirectTimeout = null;
      }
    }

    hasValidSelection(index) {
      const question = this.questions[index];
      if (!question) return false;
      const state = this.answers[question.key];
      return Boolean(state && Array.isArray(state.values) && state.values.length);
    }

    updateStepActions() {
      if (!this.nextButton) return;
      const isLast = this.currentQuestionIndex >= this.questions.length - 1;
      const valid = this.hasValidSelection(this.currentQuestionIndex);
      this.nextButton.textContent = isLast ? this.ui.finishLabel : this.ui.nextLabel;
      this.nextButton.disabled = !valid;
      // Quiet hint so the disabled "Weiter" isn't a silent dead end.
      if (this.hintEl) {
        if (valid) {
          this.hintEl.hidden = true;
          this.hintEl.textContent = '';
        } else {
          this.hintEl.textContent = this.ui.selectHint || '';
          this.hintEl.hidden = false;
        }
      }
    }

    escapeAttr(value) {
      return escapeAttr(value);
    }

    escapeHtml(value) {
      return escapeHtml(value);
    }

    formatString(template, vars) {
      return String(template).replace(/\{(\w+)\}/g, (match, key) => {
        return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match;
      });
    }

    destroy() {
      this.clearRedirectTimeout();
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('error', this.handleImageError, true);
    }

    static initAll(root) {
      const scope = root || document;
      const quizzes = scope.querySelectorAll('[data-pmgw-style-quiz]');
      quizzes.forEach((el) => {
        if (el.dataset.pmgwStyleQuizInitialized === 'true') return;
        const instance = new PMGWStyleQuiz(el);
        el.dataset.pmgwStyleQuizInitialized = 'true';
        PMGWStyleQuiz.instances.set(el, instance);
      });
    }
  }

  PMGWStyleQuiz.instances = new WeakMap();

  /*
   * Scoring weights for the inline result view. PMGWStyleQuiz scores the
   * product pool (rendered in the same section) against the shopper's answers
   * and shows a curated hero + complements in place of the quiz — no page
   * navigation. Never empty: the top matches always rank.
   */
  const RESULT_WEIGHTS = {
    collectionMatch: 3,  // Q1 dress look: per matching collection (equal)
    focusType: 4,        // Q3 jewellery type
    effectTag: 2,        // effect question (Elegant / Glamourös tag)
    budgetIn: 2,
    budgetOut: -2
  };

  document.addEventListener('DOMContentLoaded', () => {
    PMGWStyleQuiz.initAll(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    PMGWStyleQuiz.initAll(event.target);
  });

  window.PMGWStyleQuiz = PMGWStyleQuiz;
})();
